import React, { useState } from 'react';
import Guru from '@svg/guru';
import styled from 'styled-components';

import { Button, LoadingButton } from '@common/Button';
import { DescriptionOutlined, FolderOutlined, SettingsOutlined } from '@mui/icons-material';
import { Card } from '@common/Card';
import { LoaderConfiguration, CheckDeleteFileParams, refreshLoader } from '@requests/loaders';
import { DocumentLoader } from '@common/hooks/useDocument';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { DocumentLoaders } from '@common/hooks/types';
import { LinearProgressWithLabel } from '@common/Loaders/LinearProgress';
import { Stack } from '@mui/material';
import { Label } from '@common/Label';
import { GuruCard } from '@components/Modals/Loaders/Guru/GuruFiles';
import { useQueryClient } from '@tanstack/react-query';
import { formatCronExpression } from '@common/CronSchedule';
import { TelnyxBucketFileEmbedding } from '@requests/buckets';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  p,
  pre {
    margin: 0;
    color: ${({ theme }) => theme.text.standard};
    font-size: 14px;
    max-width: 360px;
  }

  .guru-collections {
    display: flex;
    flex-direction: column;
    gap: 8px;

    p {
      max-width: 340px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }
`;

interface Props {
  loader: LoaderConfiguration | undefined;
  page: number;
  open: string | null;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  documentLoader: DocumentLoader;
  setTab: React.Dispatch<React.SetStateAction<string>>;
  refreshLoader: ({ service_id, bucket_name }: { service_id: string; bucket_name: string }) => void;
  setRemoveLoader: React.Dispatch<React.SetStateAction<string | null>>;
  checkForDeletedFiles: ({ service_id, bucket_name, files }: CheckDeleteFileParams) => void;
}

export const GuruLoader: React.FC<Props> = ({
  loader,
  open,
  setOpen,
  setTab,
  documentLoader,
  page,
  setRemoveLoader,
  checkForDeletedFiles,
}) => {
  const queryClient = useQueryClient();

  const { service_id, bucketName } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { load, loading, setTotalPages, addDocumentsToQueue, totalPages, documentsLoaded } = documentLoader;

  const embeddingQueryKey = ['globalFiles', 'embedding-files', { service_id, bucket_name: bucketName }];
  const embeddingFiles = queryClient.getQueryData<TelnyxBucketFileEmbedding[]>(embeddingQueryKey) || [];

  const [collapsed, setCollapsed] = useState(true);

  const isLoaderConfigured = loader && loader.loader === 'guru';

  const executeGuruLoader = () => {
    if (!isLoaderConfigured) return;

    const region = loader.bucket_region;
    const bucket_name = loader.bucket_name;
    const paths: any[] = loader.loader_settings.collections;
    const secret_identifier = loader.auth_secret_name;

    const onComplete = (docs: GuruCard[]) => {
      const files: File[] = docs.map((doc) => {
        const fileContent = doc.document;
        const blob = new Blob([fileContent], { type: 'text/markdown' });
        const file = new File([blob], doc.filename, { type: 'text/markdown' });
        return file;
      });

      refreshLoader({ service_id: service_id as string, bucket_name });
      addDocumentsToQueue(service_id as string, bucket_name, region, files, null, () => null);
      setTab('4');

      // Deletes files from the bucket that no longer exist in the source
      checkForDeletedFiles({
        service_id: service_id as string,
        region: loader.bucket_region,
        bucket_name,
        files: docs.map((doc) => doc.filename),
      });

      const notice = `The refresh was successful and the files are being uploaded. After uploading, the files will be re-embedded.`;
      enqueueSnackbar(notice, {
        variant: 'info',
        autoHideDuration: 10000,
      });
    };

    load(
      DocumentLoaders.Guru,
      service_id as string,
      secret_identifier,
      onComplete,
      undefined,
      paths.filter((item) => item.type === 'card').map((item) => item.id),
      paths.filter((item) => item.type === 'folder').map((item) => item.id)
    );

    setTotalPages(
      paths.filter((item) => item.type === 'card').length +
        paths
          .filter((item) => item.type === 'folder')
          .map((folder) => folder.numberOfFacts)
          .reduce((a, b) => a + b, 0)
    );
  };

  const settingsEnabled =
    Boolean(loader?.bucket_name) &&
    embeddingFiles.length > 0 &&
    embeddingFiles.every((file) => file.status === 'success');

  const expression = formatCronExpression(loader?.refresh_interval_config.value || '');
  const cronExpression =
    loader?.refresh_interval_config.value.split(' ').length === 5 ? `*   ${expression}` : expression;

  const cronDescription = [
    'Click "Settings" at the bottom of the loader to view and change this schedule.\n',
    '┌──────────────────────── second (optional)',
    '│   ┌──────────────────── minute',
    '│   │   ┌──────────────── hour',
    '│   │   │   ┌──────────── day of month',
    '│   │   │   │   ┌──────── month',
    '│   │   │   │   │   ┌──── day of week',
    '│   │   │   │   │   │',
    '│   │   │   │   │   │\n',
    cronExpression,
  ].join('\n');

  if (isLoaderConfigured) {
    const cards = loader.loader_settings.collections.filter((item: any) => item.type === 'card');
    const folders = loader.loader_settings.collections.filter((item: any) => item.type === 'folder');

    const body = (
      <Container>
        <Stack spacing='8px'>
          <Label title='Refresh Every' tooltip={cronDescription} />
          <p>{`${loader.refresh_interval_config?.value}`}</p>
        </Stack>
        <Stack spacing='8px'>
          <Label title='Updated Last' tooltip='This is the user who updated this loader last.' />
          <p>{loader.updated_by}</p>
        </Stack>
        <Stack spacing='8px'>
          <Label title='Last Refreshed' tooltip='This is a timestamp of the last time the files we reloaded.' />
          <p>{new Date(loader.last_refreshed).toLocaleString()}</p>
        </Stack>
        <Stack spacing='8px'>
          <Label
            title='Next Refresh'
            tooltip='This is a timestamp of the next scheduled execution time for this loader.'
          />
          <p>{new Date(loader.next_refresh).toLocaleString()}</p>
        </Stack>
        <Stack spacing='8px'>
          <Label
            title='Cards & Folders'
            tooltip='This is the list of cards and folders that were selected for the loader from the Guru collections.'
          />
          <div className='guru-collections'>
            {cards.map((card: any) => (
              <Stack spacing={1} key={card.id} direction='row'>
                <DescriptionOutlined sx={{ fontSize: '2ch' }} />
                <p>{card.preferredPhrase}</p>
              </Stack>
            ))}
            {folders.map((folder: any) => (
              <Stack spacing={1} key={folder.id} direction='row'>
                <FolderOutlined sx={{ fontSize: '2ch' }} />
                <p>{folder.title}</p>
              </Stack>
            ))}
          </div>
        </Stack>
      </Container>
    );

    return (
      <Card
        title='Guru'
        description='The Guru loader is configured for this bucket. This loader will handle the refreshing of all content. You can manually refresh now if required.'
        icon={<Guru height={30} color='#000' />}
        link='https://developer.getguru.com/docs/import-content'
        body={body}
        actions={
          <Stack spacing={1.5}>
            <Stack direction='row' spacing={1}>
              <Button disabled={!settingsEnabled} fullWidth onClick={() => setOpen('guru')}>
                Settings
              </Button>
              <Button disabled={loading} fullWidth onClick={() => setRemoveLoader('guru')}>
                Delete Loader
              </Button>
            </Stack>
            {((totalPages !== 0 && totalPages !== documentsLoaded) || loading) && (
              <LinearProgressWithLabel denominator={totalPages} numerator={documentsLoaded} />
            )}
            <LoadingButton
              disabled={!settingsEnabled}
              minHeight='32px'
              loading={loading}
              customColor='black'
              fullWidth
              onClick={executeGuruLoader}
            >
              Refresh Cards
            </LoadingButton>
          </Stack>
        }
      />
    );
  }

  return (
    <Card
      title='Guru'
      description='Load cards from a parent Guru folder.'
      icon={<Guru height={30} color={'#000000'} />}
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      link='https://developer.getguru.com/docs/import-content'
      actions={
        <LoadingButton
          minHeight='32px'
          fullWidth
          loading={page > 0 && open === 'guru'}
          onClick={() => setOpen('guru')}
          customColor='black'
          startIcon={<SettingsOutlined sx={{ fontSize: '2.1ch !important' }} />}
        >
          Configure
        </LoadingButton>
      }
    />
  );
};
